import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs'
//PRIMENG
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CardModule } from 'primeng/card'
import { MultiSelectModule } from 'primeng/multiselect'
//APP
import { UserService } from '../../services/user.service'
import { EditUserComponent } from '../edit-user/edit-user.component'
import { UserModel } from '../../../../core/models/user.model'
import { Router } from '@angular/router'
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { CustomCheckboxComponent } from '../../../../core/components/custom-checkbox/custom-checkbox.component'
import { DepartmentService } from '../../../departments/services/department.service'
import { ProductsService } from '../../../products/services/products.service'
import { DepartmentModel } from '../../../../core/models/department.model'
import { ProductModel } from '../../../../core/models/product.model'
import { SectorService } from '../../../sectors/services/sector.service'
import { SectorModel } from '../../../../core/models/sector.model'

interface Column {
  field: string
  header: string
}

interface SelectOption {
  name: string
  code: string
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule,
    CustomCheckboxComponent,
    MultiSelectModule,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
  private userService = inject(UserService)
  private departmentService = inject(DepartmentService)
  private productsService = inject(ProductsService)
  private sectorService = inject(SectorService)
  private router = inject(Router)
  private fb = inject(FormBuilder)

  private tableLazyLoad$ = new Subject<TableLazyLoadEvent>()
  public users$ = this.userService.users$
  public totalRecords$ = this.userService.total$
  public loading$ = this.userService.loading$

  public departments: SelectOption[] = []
  public products: ProductModel[] = []
  public sectors: SectorModel[] = []

  public filtersForm = this.fb.group({
    societyKeys: [[]],
    departments: [[]], // Renamed for consistency
    products: [[]],
    sectors: [[]],
    search: [''],
  })

  public cols: Column[] = [
    { field: 'id', header: 'Id' },
    { field: 'initials', header: 'Initiales' },
    { field: 'lastname', header: 'Nom de famille' },
    { field: 'firstname', header: 'Prénom' },
    { field: 'email', header: 'Email' },
  ]

  mlSocieties = Object.values(MlSocieties)

  ngOnInit() {
    this.loadDepartments()
    this.loadProducts()
    this.loadSectors()
  }

  /**
   * Réinitialise tous les filtres du formulaire
   */
  resetFilters() {
    this.filtersForm.reset({
      societyKeys: null,
      departments: null,
      products: null,
      sectors: null,
      search: '',
    })
  }

  private loadDepartments() {
    this.departmentService.getAll().subscribe((departments) => {
      this.departments = departments.map((dept) => ({
        name: dept.name,
        code: dept.code,
      }))
    })
  }

  private loadProducts() {
    this.productsService.getProducts().subscribe((products) => {
      this.products = products
    })
  }

  private loadSectors() {
    this.sectorService.getAll().subscribe((sectors) => {
      this.sectors = sectors
    })
  }

  constructor() {
    combineLatest([
      this.tableLazyLoad$,
      this.filtersForm.valueChanges.pipe(startWith(this.filtersForm.value)),
    ])
      .pipe(
        takeUntilDestroyed(),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(([tableLazyLoad, filters]) => {
          const currentPage = tableLazyLoad.first! / tableLazyLoad.rows! + 1
          return this.userService.getUsers({
            size: tableLazyLoad.rows!,
            page: currentPage,
            search: filters.search,
            societyKeys: filters.societyKeys,
            departments: filters.departments, // Renamed for consistency
            products: filters.products,
            sectors: filters.sectors,
          })
        })
      )
      .subscribe()
  }

  //transform event to observable
  lazyLoadEmit(event: TableLazyLoadEvent) {
    this.tableLazyLoad$.next(event)
  }

  show(user: UserModel) {
    this.router.navigate(['users', user.id])
  }

  getSocietiesForUser(user: UserModel) {
    const societies = []
    if (user.idAbm) societies.push('abm')
    if (user.idNrj) societies.push('nrj')
    if (user.idBb) societies.push('bb')
    return societies
  }

  getUserSource(user: UserModel) {
    return 'tll tll-24 fa-logo-' + user.erpSource + ' text-' + user.erpSource
  }
}
